import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Clock,
  Send,
  AlertCircle,
  Loader2,
  Lock,
} from "lucide-react";
import { jobsData } from "../data/jobsData";
import ApplicationSuccessModal from "../components/careers/SuccessModal";
import ErrorModal from "../components/careers/ErrorModal";
import ResumeUploader from "../components/careers/ResumeUploader";
import {
  useSubmitJobApplication,
  useParseResume,
  useGeoLocation,
} from "../../dashboard/lib/api/careers";

// ==================== TYPES ====================

interface ApplicationFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  experience: string;
  availability: string;
  coverLetter: string;
  resume: File | null;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  experience?: string;
  availability?: string;
}

interface AutoFilledFields {
  firstName: boolean;
  lastName: boolean;
  email: boolean;
  phone: boolean;
  experience: boolean;
}

interface GeoData {
  ip: string;
  city: string;
  region: string;
  country: string;
  countryCode: string;
  timezone: string;
  latitude: number;
  longitude: number;
}

interface ErrorModalState {
  isOpen: boolean;
  title: string;
  message: string;
  type: "location" | "general";
}

// ==================== CONSTANTS ====================

const SUPPORTED_COUNTRY_CODES = ["US", "GB", "CA"];

const initialFormData: ApplicationFormData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  experience: "",
  availability: "",
  coverLetter: "",
  resume: null,
};

const initialAutoFilled: AutoFilledFields = {
  firstName: false,
  lastName: false,
  email: false,
  phone: false,
  experience: false,
};

const initialErrorModal: ErrorModalState = {
  isOpen: false,
  title: "",
  message: "",
  type: "general",
};

// ==================== HELPERS ====================

// const mapExperienceToOption = (experience?: string): string => {
//   if (!experience) return "";

//   const expLower = experience.toLowerCase();
//   const yearsMatch = expLower.match(/(\d+)/);

//   if (yearsMatch) {
//     const years = parseInt(yearsMatch[1]);
//     if (years <= 1) return "entry";
//     if (years <= 2) return "1-2";
//     if (years <= 5) return "3-5";
//     return "5+";
//   }

//   if (expLower.includes("entry") || expLower.includes("junior")) return "entry";
//   if (expLower.includes("senior") || expLower.includes("lead")) return "5+";
//   if (expLower.includes("mid")) return "3-5";

//   return "";
// };

const formatLocationFromGeo = (geo: GeoData): string => {
  if (geo.city && geo.region && geo.countryCode) {
    return `${geo.city}, ${geo.region}, ${geo.countryCode}`;
  }
  if (geo.city && geo.country) {
    return `${geo.city}, ${geo.country}`;
  }
  if (geo.country) {
    return geo.country;
  }
  return "";
};

const isCountrySupported = (countryCode: string): boolean => {
  return SUPPORTED_COUNTRY_CODES.includes(countryCode?.toUpperCase());
};

// ==================== COMPONENT ====================

const ApplicationPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const job = jobsData.find((j) => j.id === id);

  const [formData, setFormData] =
    useState<ApplicationFormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [autoFilled, setAutoFilled] =
    useState<AutoFilledFields>(initialAutoFilled);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState("");
  const [parseSuccess, setParseSuccess] = useState(false);
  const [geoData, setGeoData] = useState<GeoData | null>(null);
  const [errorModal, setErrorModal] =
    useState<ErrorModalState>(initialErrorModal);

  const { mutateAsync: submitApplication, isPending } =
    useSubmitJobApplication();
  const { isPending: isParsing } = useParseResume();
  const { data: locationData, isLoading: isLoadingLocation } = useGeoLocation();

  // Set geo data when loaded
  useEffect(() => {
    if (locationData) {
      setGeoData(locationData);
    }
  }, [locationData]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    if (!job) {
      navigate("/careers");
    }
  }, [job, navigate]);

  // Apply parsed resume data to form
  // const applyParsedData = useCallback((data: ParsedResumeData) => {
  //   const newAutoFilled: AutoFilledFields = { ...initialAutoFilled };

  //   setFormData((prev) => {
  //     const updated = { ...prev };

  //     if (data.firstName && !prev.firstName) {
  //       updated.firstName = data.firstName;
  //       newAutoFilled.firstName = true;
  //     }
  //     if (data.lastName && !prev.lastName) {
  //       updated.lastName = data.lastName;
  //       newAutoFilled.lastName = true;
  //     }
  //     if (data.email && !prev.email) {
  //       updated.email = data.email;
  //       newAutoFilled.email = true;
  //     }
  //     if (data.phone && !prev.phone) {
  //       updated.phone = data.phone;
  //       newAutoFilled.phone = true;
  //     }
  //     if (data.experience && !prev.experience) {
  //       const mappedExp = mapExperienceToOption(data.experience);
  //       if (mappedExp) {
  //         updated.experience = mappedExp;
  //         newAutoFilled.experience = true;
  //       }
  //     }

  //     return updated;
  //   });

  //   setAutoFilled((prev) => ({ ...prev, ...newAutoFilled }));
  //   setErrors((prev) => {
  //     const updated = { ...prev };
  //     Object.keys(newAutoFilled).forEach((key) => {
  //       if (newAutoFilled[key as keyof AutoFilledFields]) {
  //         delete updated[key as keyof FormErrors];
  //       }
  //     });
  //     return updated;
  //   });
  // }, []);

  // Handle resume change with auto-parsing
  const handleResumeChange = useCallback(async (file: File | null) => {
    setFormData((prev) => ({ ...prev, resume: file }));
    setParseSuccess(false);

    // Uncomment when backend is ready
    // if (file) {
    //   try {
    //     const parsedData = await parseResume(file);
    //     applyParsedData(parsedData);
    //     setParseSuccess(true);
    //   } catch (error) {
    //     console.error("Resume parsing failed:", error);
    //   }
    // } else {
    //   setAutoFilled(initialAutoFilled);
    // }
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (autoFilled[name as keyof AutoFilledFields]) {
      setAutoFilled((prev) => ({ ...prev, [name]: false }));
    }

    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    if (!formData.experience)
      newErrors.experience = "Please select experience level";
    if (!formData.availability)
      newErrors.availability = "Please select availability";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const showErrorModal = (
    title: string,
    message: string,
    type: "location" | "general" = "general"
  ) => {
    setErrorModal({
      isOpen: true,
      title,
      message,
      type,
    });
  };

  const closeErrorModal = () => {
    setErrorModal(initialErrorModal);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if geo data is available
    if (!geoData) {
      showErrorModal(
        "Location Required",
        "We couldn't detect your location. Please enable location services or try refreshing the page.",
        "location"
      );
      return;
    }

    // Check if country is supported
    if (!isCountrySupported(geoData.countryCode)) {
      showErrorModal(
        "Location Not Supported",
        `Sorry, we don't currently serve your country (${geoData.country}).`,
        "location"
      );
      return;
    }

    // Validate form fields
    if (!validateForm()) {
      const firstError = document.querySelector(".text-red-500");
      firstError?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const location = formatLocationFromGeo(geoData);

    try {
      await submitApplication({
        jobId: job!.id,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        location: location,
        experience: formData.experience,
        availability: formData.availability,
        coverLetter: formData.coverLetter || undefined,
        resume: formData.resume,
        // Include geo data in payload
        geoIp: geoData.ip,
        geoCity: geoData.city,
        geoRegion: geoData.region,
        geoCountry: geoData.country,
        geoCountryCode: geoData.countryCode,
        geoTimezone: geoData.timezone,
        geoLatitude: geoData.latitude.toString(),
        geoLongitude: geoData.longitude.toString(),
      });

      setIsSubmitted(true);
    } catch (error: any) {
      // Extract error message from API response
      const errorMessage =
        error?.response?.data?.fields?.[0]?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong. Please try again.";

      // Check if it's a location-related error
      const isLocationError =
        errorMessage.toLowerCase().includes("country") ||
        errorMessage.toLowerCase().includes("location") ||
        errorMessage.toLowerCase().includes("serve");

      showErrorModal(
        isLocationError ? "Location Not Supported" : "Application Failed",
        errorMessage,
        isLocationError ? "location" : "general"
      );
    }
  };

  const handleSubmitAnother = () => {
    setFormData(initialFormData);
    setErrors({});
    setAutoFilled(initialAutoFilled);
    setIsSubmitted(false);
    setApplicationId("");
    setParseSuccess(false);
  };

  // Helper to render input with auto-fill badge
  const renderAutoFillBadge = (field: keyof AutoFilledFields) => {
    if (!autoFilled[field]) return null;
    return (
      <span className="ml-2 text-xs text-[#8B52FF] bg-[#8B52FF]/10 px-2 py-0.5 rounded-full">
        Auto-filled
      </span>
    );
  };

  // Helper to get input class based on state
  const getInputClass = (field: keyof FormErrors) => {
    const base =
      "w-full pl-10 pr-4 py-3 rounded-xl border bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] transition-all";
    if (errors[field]) return `${base} border-red-300`;
    if (autoFilled[field as keyof AutoFilledFields])
      return `${base} border-[#8B52FF]/30`;
    return `${base} border-gray-200`;
  };

  const getSelectClass = (field: keyof FormErrors) => {
    const base =
      "w-full px-4 py-3 rounded-xl border bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] transition-all";
    if (errors[field]) return `${base} border-red-300`;
    if (autoFilled[field as keyof AutoFilledFields])
      return `${base} border-[#8B52FF]/30`;
    return `${base} border-gray-200`;
  };

  // Get formatted location for display
  const displayLocation = geoData ? formatLocationFromGeo(geoData) : "";

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#8B52FF]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate(`/careers/${job.id}`)}
            className="flex items-center gap-2 text-gray-600 hover:text-[#8B52FF] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Job Details
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Job Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] rounded-2xl p-6 mb-8 text-white"
        >
          <p className="text-white/80 text-sm mb-1">Applying for</p>
          <h1 className="text-2xl font-bold mb-2">{job.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-white/90">
            <span className="flex items-center gap-1">
              <Briefcase className="w-4 h-4" />
              {job.payRange}
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {job.locationType}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {job.schedule}
            </span>
          </div>
        </motion.div>

        {/* Application Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Resume Upload - First */}
            <ResumeUploader
              file={formData.resume}
              onFileChange={handleResumeChange}
              isParsing={isParsing}
              parseSuccess={parseSuccess}
            />

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-4 text-sm text-gray-500">
                  Your Information
                </span>
              </div>
            </div>

            {/* Name Fields */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name <span className="text-red-500">*</span>
                  {renderAutoFillBadge("firstName")}
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={getInputClass("firstName")}
                    placeholder="John"
                  />
                </div>
                {errors.firstName && (
                  <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {errors.firstName}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name <span className="text-red-500">*</span>
                  {renderAutoFillBadge("lastName")}
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={getInputClass("lastName")}
                    placeholder="Doe"
                  />
                </div>
                {errors.lastName && (
                  <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {errors.lastName}
                  </p>
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address <span className="text-red-500">*</span>
                {renderAutoFillBadge("email")}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={getInputClass("email")}
                  placeholder="john@example.com"
                />
              </div>
              {errors.email && (
                <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {errors.email}
                </p>
              )}
            </div>

            {/* Phone & Location */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                  {renderAutoFillBadge("phone")}
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={getInputClass("phone")}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
                {errors.phone && (
                  <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {errors.phone}
                  </p>
                )}
              </div>

              {/* Location - Read Only */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                  {isLoadingLocation ? (
                    <span className="ml-2 text-xs text-gray-400 inline-flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Detecting...
                    </span>
                  ) : (
                    <span className="ml-2 text-xs text-gray-400 inline-flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      Auto-detected
                    </span>
                  )}
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={displayLocation}
                    disabled
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 bg-gray-100 text-gray-600 cursor-not-allowed"
                    placeholder={
                      isLoadingLocation
                        ? "Detecting location..."
                        : "Location unavailable"
                    }
                  />
                </div>
                {/* <p className="text-xs text-gray-400 mt-1">
                  Your location is automatically detected for verification
                  purposes.
                </p> */}
              </div>
            </div>

            {/* Experience & Availability */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Experience Level <span className="text-red-500">*</span>
                  {renderAutoFillBadge("experience")}
                </label>
                <select
                  name="experience"
                  value={formData.experience}
                  onChange={handleInputChange}
                  className={getSelectClass("experience")}
                >
                  <option value="">Select level</option>
                  <option value="entry">Entry Level</option>
                  <option value="1-2">1-2 Years</option>
                  <option value="3-5">3-5 Years</option>
                  <option value="5+">5+ Years</option>
                </select>
                {errors.experience && (
                  <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {errors.experience}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Availability <span className="text-red-500">*</span>
                </label>
                <select
                  name="availability"
                  value={formData.availability}
                  onChange={handleInputChange}
                  className={getSelectClass("availability")}
                >
                  <option value="">Select availability</option>
                  <option value="immediate">Immediate</option>
                  <option value="1-2-weeks">1-2 Weeks</option>
                  <option value="2-4-weeks">2-4 Weeks</option>
                  <option value="1-month">1+ Month</option>
                </select>
                {errors.availability && (
                  <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {errors.availability}
                  </p>
                )}
              </div>
            </div>

            {/* Cover Letter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cover Letter <span className="text-gray-400">(Optional)</span>
              </label>
              <textarea
                name="coverLetter"
                value={formData.coverLetter}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] transition-all resize-none"
                placeholder="Tell us why you're interested in this role..."
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <button
                type="button"
                onClick={() => navigate(`/careers/${job.id}`)}
                className="flex-1 px-6 py-4 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isPending || isLoadingLocation}
                className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold hover:shadow-lg hover:shadow-[#8B52FF]/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isPending ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : isLoadingLocation ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Detecting Location...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit Application
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>

      {/* Success Modal */}
      <ApplicationSuccessModal
        isOpen={isSubmitted}
        applicationId={applicationId}
        jobTitle={job.title}
        email={formData.email}
        onSubmitAnother={handleSubmitAnother}
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={closeErrorModal}
        title={errorModal.title}
        message={errorModal.message}
        type={errorModal.type}
      />
    </div>
  );
};

export default ApplicationPage;
